/********************************************************************
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this
 * file in accordance with the terms of the Adobe license agreement
 * accompanying it.
 *******************************************************************/

import { IntlContext, Lang } from '@adobe-commerce/elsie/i18n';
import {
  cloneElement,
  ComponentChildren,
  createElement,
  RefObject,
  VNode,
} from 'preact';
import { Children, HTMLAttributes, isValidElement } from 'preact/compat';
import {
  StateUpdater,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'preact/hooks';
import { SlotQueueContext } from './render';

import '@adobe-commerce/elsie/components/UIProvider/debugger.css';

type MutateElement = (elem: HTMLElement) => void;

interface State {
  get: (key: string) => void;
  set: (key: string, value: any) => void;
}

interface SlotElement {
  appendChild: MutateElement;
  prependChild: MutateElement;
  appendSibling: MutateElement;
  prependSibling: MutateElement;
  remove: () => void;
}

interface PrivateContext<T> {
  _setProps: (s: StateUpdater<{}>) => void;
  _registerMethod: (
    cb: (next: T & DefaultSlotContext<T>, state: State) => void
  ) => void;
  // eslint-disable-next-line no-undef
  _htmlElementToVNode: (element: HTMLElement, tag: keyof HTMLElementTagNameMap) => VNode;
}

interface DefaultSlotContext<T> extends PrivateContext<T> {
  dictionary: Lang;
  getSlotElement: (key: string) => SlotElement;
  replaceWith: MutateElement;
  appendChild: MutateElement;
  prependChild: MutateElement;
  appendSibling: MutateElement;
  prependSibling: MutateElement;
  remove: () => void;
  onRender: (cb: (next: T & DefaultSlotContext<T>) => void) => void;
  onChange: (cb: (next: T & DefaultSlotContext<T>) => void) => void;
}

type Context<T> = T & ThisType<DefaultSlotContext<T>>; // NOSONAR

export type SlotProps<T = any> = (
  ctx: T & DefaultSlotContext<T>,
  element: HTMLDivElement | null
) => Promise<void> | void;

export type SlotMethod<P = any> = (
  callback: (next: unknown, state: State) => P
) => void;

// Slot Hook
export function useSlot<K, V extends HTMLElement>(
  name: string,
  // @ts-ignore
  context: Context<K> = {},
  callback?: SlotProps<K>,
  children?: ComponentChildren,
  render?: Function,
  // eslint-disable-next-line no-undef
  contentTag: keyof HTMLElementTagNameMap = 'div'
): [RefObject<V>, Record<string, any>, 'loading' | 'pending' | 'ready'] {
  const slotsQueue = useContext(SlotQueueContext);

  // HTML Element
  const elementRef = useRef<V>(null);

  const status = useRef<'pending' | 'ready' | 'loading'>('pending');

  // Methods
  const methodsRef = useRef<any>([]);

  // Children VNodes
  const [props, _setProps] = useState<Record<string, any>>({
    children: [children],
  });

  // Attributes
  const [_state, setState] = useState<any>({});

  const state = useMemo(
    () => ({
      get: (key: string) => _state[key],
      set: (key: string, value: any) => {
        setState({ ...state, [key]: value });
      },
    }),
    [_state]
  );

  /** Internationalization */
  // @ts-ignore
  const { intl }: any = useContext(IntlContext);

  // @ts-ignore
  context.dictionary = intl.dictionary;

  /** Privates */
  // @ts-ignore
  context._setProps = (next: State<any>) => {
    _setProps((prev) => {
      // next props
      const _next = typeof next === 'function' ? next(prev) : next;

      // On render method...
      if (render) {
        const renderNode = cloneElement(render(prev), { key: 'render' });

        // find index of existing render node
        const index = prev.children.findIndex((n: any) => n?.key === 'render');

        // replace existing render node
        prev.children[index] = renderNode;
      }

      return _next;
    });
  };

  const _registerMethod = useCallback((cb: Function) => {
    if (typeof cb === 'function') {
      methodsRef.current.push(cb);
    } else {
      console.warn('Skipped: Invalid _registerMethod', cb);
    }
  }, []);

  // @ts-ignore
  context._registerMethod = _registerMethod;

  const _htmlElementToVNode = useCallback(
    (elem: HTMLElement) => {
      return createElement(
        contentTag,
        {
          'data-slot-html-element': elem.tagName.toLowerCase(),
          ref: (refElem: HTMLElement | null): void => {
            refElem?.appendChild(elem);
          },
        },
        null
      );
    },
    [contentTag]
  );

  // @ts-ignore
  context._htmlElementToVNode = _htmlElementToVNode;

  /** Prebuilt Methods */
  // @ts-ignore
  context.getSlotElement = useCallback(
    (key: string) => {
      const element = elementRef.current?.querySelector(
        `[data-slot-key="${key}"]`
      );

      log(
        `🟦 "${name}" Slot getSlotElement: ${key}`,
        elementRef.current?.cloneNode(true),
        element?.cloneNode(true)
      );

      if (!element) return;

      return {
        appendChild: (elem: HTMLElement) => {
          element.appendChild(elem);
        },

        prependChild: (elem: HTMLElement) => {
          element.insertBefore(elem, element.firstChild);
        },

        appendSibling: (elem: HTMLElement) => {
          const parent = element.parentNode;
          parent?.insertBefore(elem, element.nextSibling);
        },

        prependSibling: (elem: HTMLElement) => {
          const parent = element.parentNode;
          parent?.insertBefore(elem, element);
        },

        remove: () => {
          element.remove();
        },
      };
    },
    [name]
  );

  // @ts-ignore
  context.onRender = useCallback((cb: Function) => {
    methodsRef.current.push(cb);
  }, []);

  /**
   * @deprecated Use `onRender` instead.
   */
  // @ts-ignore
  context.onChange = context.onRender;

  // @ts-ignore
  context.replaceWith = useCallback(
    (elem: HTMLElement) => {
      // @ts-ignore
      _registerMethod((next) => {
        // @ts-ignore
        const children = _htmlElementToVNode(elem);

        next._setProps({ children: [children] });
      });
    },
    [_htmlElementToVNode, _registerMethod]
  );

  // @ts-ignore
  context.appendChild = useCallback(
    (elem: HTMLElement) => {
      // @ts-ignore
      _registerMethod((next) => {
        // @ts-ignore
        const vnode = _htmlElementToVNode(elem);

        next._setProps((prev: any) => {
          return {
            ...prev,
            children: [...prev.children, vnode],
          };
        });
      });
    },
    [_htmlElementToVNode, _registerMethod]
  );

  // @ts-ignore
  context.prependChild = useCallback(
    (elem: HTMLElement) => {
      // @ts-ignore
      _registerMethod((next) => {
        // @ts-ignore
        const vnode = _htmlElementToVNode(elem);

        next._setProps((prev: any) => {
          return {
            ...prev,
            children: [vnode, ...prev.children],
          };
        });
      });
    },
    [_htmlElementToVNode, _registerMethod]
  );

  // @ts-ignore
  context.appendSibling = useCallback(
    (elem: HTMLElement) => {
      // @ts-ignore
      _registerMethod(() => {
        const parent = elementRef.current?.parentNode;

        parent?.insertBefore(elem, elementRef.current?.nextSibling ?? null);
      });
    },
    [_registerMethod]
  );

  // @ts-ignore
  context.prependSibling = useCallback(
    (elem: HTMLElement) => {
      // @ts-ignore
      _registerMethod(() => {
        const parent = elementRef.current?.parentNode;

        parent?.insertBefore(elem, elementRef.current);
      });
    },
    [_registerMethod]
  );

  // @ts-ignore
  context.remove = useCallback(() => {
    // @ts-ignore
    _registerMethod(() => {
      elementRef.current?.remove();
    });
  }, [_registerMethod]);

  const handleLifeCycleRender = useCallback(async () => {
    if (status.current === 'loading') return;

    status.current = 'loading';

    log(`🟨 "${name}" Slot Rendered`);

    // Reset
    const renderNode = render
      ? cloneElement(render(props), { key: 'render' })
      : null;
    _setProps({ children: [renderNode ?? children] });

    // Run all registered methods
    methodsRef.current.forEach((method: any) => {
      method(context, state);
    });

    status.current = 'ready';
  }, [children, context, name, props, render, state]);

  // Initialization
  const handleLifeCycleInit = useCallback(async () => {
    if (!callback) return;

    try {
      status.current = 'loading';

      log(`🟩 "${name}" Slot Initialized`);
      await callback(
        context as K & DefaultSlotContext<K>,
        elementRef.current as HTMLDivElement | null
      );
    } catch (error) {
      console.error(`Error in "${callback.name}" Slot callback`, error);
    } finally {
      status.current = 'ready';

      // first render
      await handleLifeCycleRender();
    }
  }, [callback, context, handleLifeCycleRender, name]);

  // Initialization
  useEffect(() => {
    handleLifeCycleInit().finally(() => {
      if (slotsQueue && slotsQueue.value.has(name)) {
        slotsQueue.value.delete(name);
        slotsQueue.value = new Set(slotsQueue.value);
      }
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update
  useEffect(() => {
    handleLifeCycleRender();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(context), JSON.stringify(_state)]);

  return [elementRef, props, status.current];
}

/**
 * Recursively processes children elements to conditionally prevent image loading.
 * 
 * This function traverses the children tree and modifies img elements to prevent
 * premature loading during slot initialization. When isReady is false, img src
 * attributes are set to empty string to prevent network requests, while preserving
 * the original src in a data attribute for debugging purposes.
 * 
 * @param children - The children elements to process (can be any React/Preact children)
 * @param isReady - Whether the slot is ready to load images (true) or should prevent loading (false)
 * @returns Processed children with conditional image src attributes
 */
const processChildren = (children: any, isReady: boolean): any => {
  return Children.map(children, child => {
    // Handle text nodes, numbers, etc.
    if (!isValidElement(child)) {
      return child;
    }

    // Handle img elements - conditionally set src
    if (child.props.src) {
      return cloneElement(child, {
        ...child.props,
        src: isReady ? child.props.src : "",
        // Optionally preserve original src in data attribute for debugging
        'data-original-src': child.props.src,
      });
    }

    // Handle elements with children - recursively process them
    if (child.props && child.props.children) {
      return cloneElement(child, {
        ...child.props,
        children: processChildren(child.props.children, isReady),
      });
    }

    // Return other elements as-is
    return child;
  });
};

// Slot Component
interface SlotPropsComponent<T>
  extends Omit<HTMLAttributes<HTMLElement>, 'slot'> {
  name: string;
  lazy?: boolean;
  slot?: SlotProps<T>;
  context?: Context<T>;
  render?: (props: Record<string, any>) => VNode | VNode[];
  // eslint-disable-next-line no-undef
  slotTag?: keyof HTMLElementTagNameMap; // The tag for the slot wrapper itself
  // eslint-disable-next-line no-undef
  contentTag?: keyof HTMLElementTagNameMap; // The tag for dynamically inserted content
  children?: ComponentChildren;
}

export function Slot<T>({
  name,
  lazy = false,
  context,
  slot,
  children,
  render,
  slotTag = 'div',
  contentTag = 'div',
  ...props
}: Readonly<SlotPropsComponent<T>>): VNode<{
  ref: RefObject<HTMLElement>;
  'data-slot': string;
  [key: string]: any;
}> {
  const slotsQueue = useContext(SlotQueueContext);

  const [elementRef, slotProps, status] = useSlot<T, HTMLElement>(
    name,
    context,
    slot,
    children,
    render,
    contentTag
  );

  useMemo(() => {
    if (!name) {
      return console.warn('Slot "name" is required');
    }

    // add slot to queue
    if (slotsQueue && lazy === false) {
      slotsQueue.value.add(name);
      slotsQueue.value = new Set(slotsQueue.value);
    }
  }, [name, lazy, slotsQueue]);

  return createElement(
    slotTag,
    {
      ...props,
      ref: elementRef,
      'data-slot': name,
    },
    processChildren(slotProps.children, status === 'ready')
  );
}

// Debugger

// @ts-ignore
window.DROPINS = window.DROPINS || {};

// @ts-ignore
window.DROPINS.showSlots = async (state) => {
  // cache state in session storage
  window.sessionStorage.setItem(
    'dropin-debugger--show-slots',
    state.toString()
  );

  document.body.classList.toggle('dropin-debugger--show-slots', state);
};

let log: (...attrs: any) => void = () => {};

// @ts-ignore
window.DROPINS.logSlots = async (state) => {
  // cache state in session storage
  window.sessionStorage.setItem('dropin-debugger--log-slots', state.toString());

  log = state ? (...attrs) => console.log(...attrs) : () => {};
};

/** Persistent Settings */

// @ts-ignore
window.DROPINS.showSlots(
  window.sessionStorage.getItem('dropin-debugger--show-slots') === 'true'
);

// @ts-ignore
window.DROPINS.logSlots(
  window.sessionStorage.getItem('dropin-debugger--log-slots') === 'true'
);
